import  { useState } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  Grid, 
  TextField, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Stepper, 
  Step, 
  StepLabel,
  Chip,
  Paper,
  
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { 
  CheckCircleOutline as ApproveIcon, 
  HighlightOff as RejectIcon,
  AssignmentLate as PendingIcon,
  PendingActions as ReviewIcon
} from '@mui/icons-material';
import Layout from '../components/layout/Calayout';

const ReviewWorkflowPage = () => {
  // Review submission state
  const [reviewDetails, setReviewDetails] = useState({
    documentType: '',
    reviewer: '',
    assignedTo: '',
    priority: ''
  });

  // Review workflow state
  const [reviewSteps, setReviewSteps] = useState([
    { 
      id: 1, 
      name: 'Initial Review', 
      status: 'pending', 
      assignedTo: '',
      comments: ''
    },
    { 
      id: 2, 
      name: 'Technical Verification', 
      status: 'not_started', 
      assignedTo: '',
      comments: ''
    },
    { 
      id: 3, 
      name: 'Final Approval', 
      status: 'not_started', 
      assignedTo: '',
      comments: ''
    }
  ]);

  // Current active review step
  const [activeStep, setActiveStep] = useState(0);

  // Dialog state for review actions
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentAction, setCurrentAction] = useState(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(null);

  // Handle step action (approve/reject)
  const handleStepAction = (action, stepIndex) => {
    const updatedSteps = [...reviewSteps];
    updatedSteps[stepIndex] = {
      ...updatedSteps[stepIndex],
      status: action,
      assignedTo: reviewDetails.reviewer
    };

    // Move to next step if approved
    if (action === 'approved' && stepIndex < reviewSteps.length - 1) {
      setActiveStep(stepIndex + 1);
    }

    setReviewSteps(updatedSteps);
    setDialogOpen(false);
  };

  // Render step status chip
  const renderStepStatusChip = (status) => {
    switch (status) {
      case 'pending':
        return <Chip icon={<PendingIcon />} label="Pending" color="warning" />;
      case 'approved':
        return <Chip icon={<ApproveIcon />} label="Approved" color="success" />;
      case 'rejected':
        return <Chip icon={<RejectIcon />} label="Rejected" color="error" />;
      case 'not_started':
      default:
        return <Chip icon={<ReviewIcon />} label="Not Started" color="default" />;
    }
  };

  // Open action dialog
  const openActionDialog = (action, stepIndex) => {
    setCurrentAction(action);
    setCurrentStepIndex(stepIndex);
    setDialogOpen(true);
  };

  // Reset workflow
  const resetWorkflow = () => {
    setReviewSteps([
      { 
        id: 1, 
        name: 'Initial Review', 
        status: 'pending', 
        assignedTo: '',
        comments: ''
      },
      { 
        id: 2, 
        name: 'Technical Verification', 
        status: 'not_started', 
        assignedTo: '',
        comments: ''
      },
      { 
        id: 3, 
        name: 'Final Approval', 
        status: 'not_started', 
        assignedTo: '',
        comments: ''
      }
    ]);
    setActiveStep(0);
    setReviewDetails({
      documentType: '',
      reviewer: '',
      assignedTo: '',
      priority: ''
    });
  };

  return (
    <Layout>
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Review Workflow
          </Typography>

          {/* Review Details Input */}
          <Grid container spacing={3} mt={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Document Type</InputLabel>
                <Select
                  value={reviewDetails.documentType}
                  onChange={(e) => setReviewDetails(prev => ({
                    ...prev,
                    documentType: e.target.value
                  }))}
                  label="Document Type"
                  required
                >
                  <MenuItem value="Income Tax Return">Income Tax Return</MenuItem>
                  <MenuItem value="GST Audit">GST Audit</MenuItem>
                  <MenuItem value="Financial Statement">Financial Statement</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Assigned Reviewer"
                variant="outlined"
                value={reviewDetails.reviewer}
                onChange={(e) => setReviewDetails(prev => ({
                  ...prev,
                  reviewer: e.target.value
                }))}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Assign To"
                variant="outlined"
                value={reviewDetails.assignedTo}
                onChange={(e) => setReviewDetails(prev => ({
                  ...prev,
                  assignedTo: e.target.value
                }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Priority</InputLabel>
                <Select
                  value={reviewDetails.priority}
                  onChange={(e) => setReviewDetails(prev => ({
                    ...prev,
                    priority: e.target.value
                  }))}
                  label="Priority"
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Review Workflow Stepper */}
          <Box mt={4}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {reviewSteps.map((step, index) => (
                <Step key={step.id}>
                  <StepLabel>
                    {step.name}
                    <Box mt={1}>
                      {renderStepStatusChip(step.status)}
                    </Box>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          {/* Detailed Review Steps */}
          <Box mt={4}>
            {reviewSteps.map((step, index) => (
              <Paper 
                key={step.id} 
                elevation={3} 
                sx={{ 
                  mb: 2, 
                  p: 2, 
                  opacity: index > activeStep ? 0.5 : 1 
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle1">{step.name}</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Comments"
                      variant="outlined"
                      multiline
                      rows={2}
                      value={step.comments}
                      onChange={(e) => {
                        const updatedSteps = [...reviewSteps];
                        updatedSteps[index].comments = e.target.value;
                        setReviewSteps(updatedSteps);
                      }}
                      disabled={index !== activeStep}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box display="flex" justifyContent="space-between">
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<ApproveIcon />}
                        onClick={() => openActionDialog('approved', index)}
                        disabled={index !== activeStep}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        startIcon={<RejectIcon />}
                        onClick={() => openActionDialog('rejected', index)}
                        disabled={index !== activeStep}
                      >
                        Reject
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Box>

          {/* Action Buttons */}
          <Box mt={3} display="flex" justifyContent="space-between">
            <Button 
              variant="outlined" 
              color="secondary"
              onClick={resetWorkflow}
            >
              Reset Workflow
            </Button>
            <Button 
              variant="contained" 
              color="primary"
              disabled={!reviewDetails.documentType || !reviewDetails.reviewer}
            >
              Submit Review
            </Button>
          </Box>

          {/* Confirmation Dialog */}
          <Dialog
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
          >
            <DialogTitle>
              {currentAction === 'approved' ? 'Approve Step' : 'Reject Step'}
            </DialogTitle>
            <DialogContent>
              <Typography>
                Are you sure you want to {currentAction} this step?
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                color={currentAction === 'approved' ? 'success' : 'error'}
                onClick={() => handleStepAction(currentAction, currentStepIndex)}
              >
                Confirm
              </Button>
            </DialogActions>
          </Dialog>
        </CardContent>
      </Card>
    </Layout>
  );
};

export default ReviewWorkflowPage;